/**
 * Map hosts to dashboard / marketing / API origins.
 *
 * Staging (hyphenated first label on .com):
 *   staging.arrangelly.com → marketing
 *   staging-api.arrangelly.com → API
 *   staging-dashboard.arrangelly.com → dashboard
 *
 * Production: arrangelly.com / www, api.arrangelly.com, dashboard.arrangelly.com.
 * Local: arrangelly.local, api.arrangelly.local, dashboard.arrangelly.local.
 *
 * Optional Vite overrides: VITE_DASHBOARD_ORIGIN, VITE_MARKETING_ORIGIN, VITE_API_BASE_URL (API wins when set).
 */

const ARR_COM = 'arrangelly.com';

function isArrangellyCom(hostname: string): boolean {
  return hostname === ARR_COM || hostname.endsWith(`.${ARR_COM}`);
}

/** Strip -dashboard / -api suffix from first label; null if not that pattern. */
function envPrefixFromCompoundFirstLabel(first: string): string | null {
  const lower = first.toLowerCase();
  if (lower.endsWith('-dashboard')) {
    return lower.slice(0, -'-dashboard'.length);
  }
  if (lower.endsWith('-api')) {
    return lower.slice(0, -'-api'.length);
  }
  return null;
}

export function getMarketingSiteOrigin(): string {
  const override = import.meta.env.VITE_MARKETING_ORIGIN?.trim();
  if (override) {
    return override.replace(/\/$/, '');
  }

  const { protocol, hostname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//arrangelly.local`;
  }

  // Production dashboard host
  if (/^dashboard\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//${ARR_COM}`;
  }

  // Legacy: dashboard.{subdomain}.arrangelly.com → marketing at {subdomain}.arrangelly.com
  if (hostname.startsWith('dashboard.') && isArrangellyCom(hostname)) {
    return `${protocol}//${hostname.slice('dashboard.'.length)}`;
  }

  const parts = hostname.split('.');
  if (parts.length === 3 && parts[1] === 'arrangelly' && parts[2] === 'com') {
    const envFromCompound = envPrefixFromCompoundFirstLabel(parts[0]);
    if (envFromCompound) {
      return `${protocol}//${envFromCompound}.${ARR_COM}`;
    }
  }

  return `${protocol}//${hostname}`;
}

export function getDashboardOrigin(): string {
  const override = import.meta.env.VITE_DASHBOARD_ORIGIN?.trim();
  if (override) {
    return override.replace(/\/$/, '');
  }

  const { protocol, hostname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//dashboard.arrangelly.local`;
  }
  if (hostname.endsWith('.arrangelly.local') || hostname === 'arrangelly.local') {
    return `${protocol}//dashboard.arrangelly.local`;
  }
  if (!hostname.endsWith(`.${ARR_COM}`) && hostname !== ARR_COM) {
    return `${protocol}//dashboard.${ARR_COM}`;
  }

  // Production marketing apex / www
  if (hostname === ARR_COM || /^www\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//dashboard.${ARR_COM}`;
  }

  // Already on production or legacy dashboard.*.com
  if (hostname.startsWith('dashboard.') && isArrangellyCom(hostname)) {
    return `${protocol}//${hostname}`;
  }

  const parts = hostname.split('.');
  if (parts.length === 3 && parts[1] === 'arrangelly' && parts[2] === 'com') {
    const first = parts[0].toLowerCase();

    if (first.endsWith('-dashboard')) {
      return `${protocol}//${hostname}`;
    }
    if (first.endsWith('-api')) {
      const env = envPrefixFromCompoundFirstLabel(parts[0])!;
      return `${protocol}//${env}-dashboard.${ARR_COM}`;
    }
    // Marketing host: {env}.arrangelly.com (not api / dashboard / www)
    if (first !== 'www' && first !== 'api' && first !== 'dashboard') {
      return `${protocol}//${first}-dashboard.${ARR_COM}`;
    }
    if (first === 'api') {
      return `${protocol}//dashboard.${ARR_COM}`;
    }
  }

  return `${protocol}//dashboard.${ARR_COM}`;
}

export function getDerivedApiBaseUrl(): string {
  const env = import.meta.env.VITE_API_BASE_URL?.trim();
  if (env) {
    return env;
  }

  const { protocol, hostname } = window.location;

  if (hostname.endsWith('.arrangelly.local') || hostname === 'arrangelly.local') {
    return `${protocol}//api.arrangelly.local`;
  }

  if (!isArrangellyCom(hostname)) {
    return '/api';
  }

  if (hostname === ARR_COM || /^www\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//api.${ARR_COM}`;
  }

  if (/^api\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//${hostname}`;
  }

  if (/^dashboard\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//api.${ARR_COM}`;
  }

  if (hostname.startsWith('dashboard.') && isArrangellyCom(hostname)) {
    const rest = hostname.slice('dashboard.'.length);
    if (rest === ARR_COM) {
      return `${protocol}//api.${ARR_COM}`;
    }
    if (rest.endsWith(`.${ARR_COM}`)) {
      const sub = rest.slice(0, -(`.${ARR_COM}`).length);
      if (sub && !sub.includes('.')) {
        return `${protocol}//${sub}-api.${ARR_COM}`;
      }
    }
    return `${protocol}//api.${rest}`;
  }

  const parts = hostname.split('.');
  if (parts.length === 3 && parts[1] === 'arrangelly' && parts[2] === 'com') {
    const first = parts[0].toLowerCase();
    if (first.endsWith('-dashboard')) {
      const env = envPrefixFromCompoundFirstLabel(parts[0])!;
      return `${protocol}//${env}-api.${ARR_COM}`;
    }
    if (first.endsWith('-api')) {
      return `${protocol}//${hostname}`;
    }
    if (first !== 'www' && first !== 'api' && first !== 'dashboard') {
      return `${protocol}//${first}-api.${ARR_COM}`;
    }
  }

  return `${protocol}//api.${ARR_COM}`;
}
